import React from "react";
import { IEphemerals } from "../../redux/types/IEphemerals";
import { IEphemeralExceptions } from "../../redux/types/IEphemeralExceptions";
import { IEphemeralAndEphemeralExceptions } from "../../redux/types/IEphemeralAndEphemeralExceptions";
import { Icon } from "@fluentui/react";
import { ContentViewRegular } from "@fluentui/react-icons";
import { SearchTextDataInput } from "../../repertoire/components/textDataInput/TextDataInput";
import DataGridTable from "../../repertoire/components/dataGridTable/DataGridTable";
import { ADD_NEW_ACTION, EPHEMERAL_EXCEPTIONS_KEY, EPHEMERALS_KEY, READ_ONLY_INPUT, REMOVE_ACTION, TEXT_INPUT } from "../../repertoire/Consts";
import { IRepertoireStateKeys } from "../../repertoire/types/IRepertoireStateKeys";
import { ITabReduxItem } from "../../redux/types/ITabReduxItem";
import { sortEphemeralsFieldData } from "../../redux/reducers/RepertoireReducer";
import ActionButton from "../../repertoire/components/actionButton/ActionButton";
import { IValidationMessage } from "../../repertoire/types/IValidationMessage";
import { MessageBanner, MessageType } from "../../repertoire/components/messageBanner/MessageBanner";


export interface IEphemeralsPageProps {
    dataGridTableData: { fields: any[] };
    getRepertoireFields: () => void;
    ephemerals?: IEphemerals[];
    ephemeralExceptions?: IEphemeralExceptions[];
    ephemeralAndEphemeralExceptionsResult?: IEphemeralAndEphemeralExceptions;
    getAllEphemeralExceptions: () => void;
    getAllEphemerals: () => void;
    sortEphemeralsFieldData: (value: any, name: IRepertoireStateKeys) => void;
    saveEphemeralAndEphemeralExceptions: (
        ephemeralAndEphemeralExceptionsResult:
            IEphemeralAndEphemeralExceptions,
        isEphemeral: boolean
    ) => void;
    ephemeralAndExceptionValidationMessages?: IValidationMessage[];
    resetMessageBanner?: () => void;
    ephemeralSaveSuccessful?: boolean;
}

export const EphemeralsPage: React.FC<IEphemeralsPageProps> = ({
    dataGridTableData,
    getRepertoireFields,
    ephemerals,
    ephemeralExceptions,
    ephemeralAndEphemeralExceptionsResult,
    getAllEphemeralExceptions,
    getAllEphemerals,
    sortEphemeralsFieldData,
    saveEphemeralAndEphemeralExceptions,
    ephemeralAndExceptionValidationMessages,
    resetMessageBanner,
    ephemeralSaveSuccessful
}) => {

    const [loaded, setLoaded] = React.useState(false);
    const [loadedEphemerals, setLoadedEphemerals] = React.useState(false);
    const [loadedEphemeralExceptions, setLoadedEphemeralExceptions] = React.useState(false);
    const [isEphemeralLoading, setIsEphemeralLoading] = React.useState(true);
    const [ephemeralsData, setEphemeralsData] = React.useState<IEphemeralAndEphemeralExceptions>({ ephemerals: [], ephemeralExceptions: [] });
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");
    const ephemeralsDataRef = React.useRef<IEphemeralAndEphemeralExceptions>({ ephemerals: [], ephemeralExceptions: [] });
    const originalDataRef = React.useRef<IEphemeralAndEphemeralExceptions>({ ephemerals: [], ephemeralExceptions: [] });
    const deletedEphemeralIdsRef = React.useRef<number[]>([]);
    const deletedEphemeralExceptionIdsRef = React.useRef<number[]>([]);
    const ephemeralSaveSuccessfulRef = React.useRef<boolean | undefined>(false);
    React.useEffect(() => {
        ephemeralSaveSuccessfulRef.current = ephemeralSaveSuccessful;
    }, [ephemeralSaveSuccessful]);

    React.useEffect(() => {
        if (!ephemerals) {
            getAllEphemerals && getAllEphemerals();
        }
        if (!ephemeralExceptions) {
            getAllEphemeralExceptions && getAllEphemeralExceptions();
        }
        if (!dataGridTableData || !dataGridTableData.fields || dataGridTableData.fields.length === 0) {
            getRepertoireFields && getRepertoireFields();
        }
    }, []);

    React.useEffect(() => {
        ephemeralsDataRef.current = ephemeralsData;
    }, [ephemeralsData]);

    React.useEffect(() => {
        if (Array.isArray(ephemerals)) {
            const updatedEphemerals = ephemerals.map(ephemeral => ({
                ...ephemeral,
                lastModifiedDate: {
                    value: formatDateTime(
                        ephemeral.lastModifiedDate.value
                    ),
                    inputType: READ_ONLY_INPUT
                }
            }));

            const data = {
                ephemerals: updatedEphemerals,
                ephemeralExceptions: ephemeralsDataRef.current.ephemeralExceptions
            };

            originalDataRef.current = data;
            ephemeralsDataRef.current = data;
            setEphemeralsData(data);
            setHasUnsavedChanges(false);
            setLoadedEphemerals(true);
            setLoaded(true);
        }
    }, [ephemerals]);

    React.useEffect(() => {
        if (Array.isArray(ephemeralExceptions)) {
            const updatedEphemeralExceptions = ephemeralExceptions.map(exception => ({
                ...exception,
                lastModifiedDate: {
                    value: formatDateTime(
                        exception.lastModifiedDate.value
                    ),
                    inputType: READ_ONLY_INPUT
                }
            }));

            const data = {
                ephemerals: ephemeralsDataRef.current.ephemerals,
                ephemeralExceptions: updatedEphemeralExceptions
            };

            originalDataRef.current = {
                ephemerals: data.ephemerals,
                ephemeralExceptions: updatedEphemeralExceptions
            };
            ephemeralsDataRef.current = data;
            setEphemeralsData(data);
            setHasUnsavedChanges(false);
            setLoadedEphemeralExceptions(true);
            setLoaded(true);
        }
    }, [ephemeralExceptions]);

    React.useEffect(() => {

        if (ephemeralAndEphemeralExceptionsResult) {
            const result = ephemeralAndEphemeralExceptionsResult;
            const updatedEphemerals = (result.ephemerals ?? ephemeralsDataRef.current.ephemerals ?? []).map(ephemeral => ({
                ...ephemeral,
                lastModifiedDate: {
                    value: formatDateTime(ephemeral.lastModifiedDate.value),
                    inputType: READ_ONLY_INPUT
                }
            }));
            const updatedEphemeralExceptions = (result.ephemeralExceptions ?? ephemeralsDataRef.current.ephemeralExceptions ?? []).map(exception => ({
                ...exception,
                lastModifiedDate: {
                    value: formatDateTime(exception.lastModifiedDate.value),
                    inputType: READ_ONLY_INPUT
                }
            }));
            const data = {
                ephemerals: updatedEphemerals,
                ephemeralExceptions: updatedEphemeralExceptions
            };
            originalDataRef.current = data;
            ephemeralsDataRef.current = data;
            setEphemeralsData(data);
            setHasUnsavedChanges(false);
            setLoadedEphemerals(true);
            setLoadedEphemeralExceptions(true);
            setLoaded(true);
        }
    }, [ephemeralAndEphemeralExceptionsResult]);

    const addNewAction = dataGridTableData.fields.find((field: any) => field.name === ADD_NEW_ACTION);

    const gridDataTable = {
        fields: [
            ...(addNewAction ? [addNewAction] : []),
            ...dataGridTableData.fields.filter(
                (field: any) => field.componentInstance === isEphemeralLoading ? EPHEMERALS_KEY : EPHEMERAL_EXCEPTIONS_KEY
            )
        ]
    };

    const changeEphemeralView = (value: boolean) => {
        setIsEphemeralLoading(value);
        setSearchTerm("");
        resetMessageBanner?.();
    };

    const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            filterEphemerals();
        }
    };

    const filterEphemerals = () => {
        const searchValue = searchTerm.trim().toLowerCase();
        const sourceData = originalDataRef.current;

        // If search is empty, restore all original data
        if (!searchValue) {
            setEphemeralsData({
                ephemerals: sourceData.ephemerals,
                ephemeralExceptions: sourceData.ephemeralExceptions
            });

            return;
        }

        const matchesSearch = (item: any) =>
            Object.values(item || {}).some((data: any) => {
                const value =
                    data !== null && typeof data === "object" && "value" in data
                        ? data.value
                        : data;

                return String(value ?? "")
                    .toLowerCase()
                    .includes(searchValue);
            });

        setEphemeralsData({
            ephemerals: sourceData.ephemerals.filter(matchesSearch),
            ephemeralExceptions: sourceData.ephemeralExceptions.filter(matchesSearch)
        });
    };

    const formatDateTime = (dateString: string): string => {
        if (!dateString) {
            return "";
        }

        const date = new Date(dateString);

        return date.toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
        }).toUpperCase();
    };

    const changeData = (value: any[], name: IRepertoireStateKeys) => {
        const currentValue = value ?? [];
        setHasUnsavedChanges(true);
        const getFieldValue = (item: any, fieldName: string) => {
            const field = item?.[fieldName];

            return field !== null && typeof field === "object" && "value" in field
                ? field.value
                : field;
        };

        if (isEphemeralLoading) {
            // Find existing ephemeral records that are no longer in the grid
            const originalEphemerals = originalDataRef.current.ephemerals;

            const currentIds = new Set(
                currentValue
                    .map((item: any) => getFieldValue(item, "ephemeralID"))
                    .filter((ephemeralID: number) => ephemeralID > 0)
            );

            const deletedIds = originalEphemerals
                .filter(
                    (item: any) =>
                        getFieldValue(item, "ephemeralID") > 0 &&
                        !currentIds.has(getFieldValue(item, "ephemeralID"))
                )
                .map((item: any) => getFieldValue(item, "ephemeralID"));

            deletedEphemeralIdsRef.current = deletedIds;

            const updatedData = currentValue.map((ephemeral: any) => ({
                ephemeralID: ephemeral.ephemeralID,
                isDeleted: ephemeral.isDeleted,
                lastModifiedDate: ephemeral.lastModifiedDate,
                title: ephemeral.title
            }));

            setEphemeralsData({
                ephemerals: updatedData,
                ephemeralExceptions:
                    ephemeralsDataRef.current.ephemeralExceptions
            });
        } else {
            // Find existing exception records that are no longer in the grid
            const originalExceptions =
                originalDataRef.current.ephemeralExceptions;

            const currentIds = new Set(
                currentValue
                    .map((item: any) => getFieldValue(item, "ephemeralExceptionsID"))
                    .filter((ephemeralExceptionsID: number) => ephemeralExceptionsID > 0)
            );

            const deletedIds = originalExceptions
                .filter(
                    (item: any) =>
                        getFieldValue(item, "ephemeralExceptionsID") > 0 &&
                        !currentIds.has(getFieldValue(item, "ephemeralExceptionsID"))
                )
                .map(
                    (item: any) =>
                        getFieldValue(item, "ephemeralExceptionsID")
                );

            deletedEphemeralExceptionIdsRef.current = deletedIds;

            const updatedData = currentValue.map((exception: any) => ({
                ephemeralExceptionsID: exception.ephemeralExceptionsID,
                isDeleted: exception.isDeleted,
                lastModifiedDate: exception.lastModifiedDate,
                title: exception.title
            }));

            setEphemeralsData({
                ephemerals: ephemeralsDataRef.current.ephemerals,
                ephemeralExceptions: updatedData
            });
        }
        resetMessageBanner?.();
    };

    const sortData = (value: any, name: IRepertoireStateKeys) => {
        sortEphemeralsFieldData(value, name as IRepertoireStateKeys);
    };

    const getFieldValue = (item: any, fieldName: string) => {
        const field = item?.[fieldName];

        return field !== null && typeof field === "object" && "value" in field
            ? field.value
            : field;
    };

    const saveChanges = () => {
        if (isEphemeralLoading) {
            const currentEphemerals = ephemeralsData.ephemerals ?? [];
            const originalEphemerals =
                originalDataRef.current.ephemerals ?? [];

            const currentIds = new Set(
                currentEphemerals
                    .map((item: any) =>
                        getFieldValue(item, "ephemeralID")
                    )
                    .filter((id: number) => id > 0)
            );

            // Existing records that were removed from the grid
            const deletedEphemerals = originalEphemerals
                .filter((item: any) => {
                    const id = getFieldValue(item, "ephemeralID");

                    return id > 0 && !currentIds.has(id);
                })
                .map((item: any) => ({
                    ...item,
                    isDeleted:
                        item.isDeleted &&
                            typeof item.isDeleted === "object"
                            ? {
                                ...item.isDeleted,
                                value: true
                            }
                            : true
                }));

            // Keep ALL current records, including new records with ID = 0
            const ephemerals = [
                ...currentEphemerals,
                ...deletedEphemerals
            ];

            saveEphemeralAndEphemeralExceptions(
                {
                    ephemerals
                },
                true
            );
        } else {
            const currentExceptions =
                ephemeralsData.ephemeralExceptions ?? [];

            const originalExceptions =
                originalDataRef.current.ephemeralExceptions ?? [];

            const currentIds = new Set(
                currentExceptions
                    .map((item: any) =>
                        getFieldValue(
                            item,
                            "ephemeralExceptionsID"
                        )
                    )
                    .filter((id: number) => id > 0)
            );

            // Existing records that were removed from the grid
            const deletedExceptions = originalExceptions
                .filter((item: any) => {
                    const id = getFieldValue(
                        item,
                        "ephemeralExceptionsID"
                    );

                    return id > 0 && !currentIds.has(id);
                })
                .map((item: any) => ({
                    ...item,
                    isDeleted:
                        item.isDeleted &&
                            typeof item.isDeleted === "object"
                            ? {
                                ...item.isDeleted,
                                value: true
                            }
                            : true
                }));

            // Keep ALL current records, including new records with ID = 0
            const ephemeralExceptions = [
                ...currentExceptions,
                ...deletedExceptions
            ];

            saveEphemeralAndEphemeralExceptions(
                {
                    ephemeralExceptions
                },
                false
            );
        }
    };
    const resetMessage = () => {
        resetMessageBanner?.();
    };
    const isGridLoaded = dataGridTableData && Array.isArray(dataGridTableData.fields) && dataGridTableData.fields.length > 0;
    const isDataLoaded = loaded && isGridLoaded && (isEphemeralLoading ? loadedEphemerals : loadedEphemeralExceptions);
    return (
        <>{isDataLoaded && <div className="container-fluid pageContent">
            <div className="row">
                <div className="">
                    <div className="tab-content formBox tab-pane active fade in scrollableContent">
                        <div className="flexRow flexJustifyContentSpaceBetween" >
                            <div className="title">{isEphemeralLoading ? "Ephemeral Term Maintenance" : "Ephemeral Exceptions Term Maintenance"}</div>
                            <div className="flexRow flexAlignItemsCenter cursorPointer" style={{ fontSize: "15px", gap: "5px" }} onClick={() => changeEphemeralView(!isEphemeralLoading)}><ContentViewRegular className="icon" aria-hidden="true" style={{ fontSize: "20px", color: "#38429C" }} />{isEphemeralLoading ? "Go to Ephemeral Exceptions Term Maintenance" : "Go to Ephemeral Term Maintenance"}</div>
                        </div>
                        <div className="package-form">
                            <div className="title" style={{ fontSize: "larger" }}>{isEphemeralLoading ? "Search Ephemeral Term:" : "Search Ephemeral Exceptions Term:"}</div>
                            <SearchTextDataInput
                                label={''}
                                value={searchTerm}
                                changeData={(value) => setSearchTerm(String(value))}
                                fieldName={"Search term"}
                                isHidden={false}
                                Width={500}
                                useEnterSubmit={true}
                                handleKeyDown={handleSearchKeyDown}
                                onSearchClick={filterEphemerals} />
                        </div>
                        <div className="paddingVeryLarge">
                            <div className="flexRow ">
                                <Icon className="icon" aria-hidden="true" iconName="Info" styles={{ root: { fontSize: "18px", color: "#38429C", marginRight: "8px" } }} />
                                <div style={{ fontSize: "14px", color: "#38429C", paddingTop: "2px" }}>To edit, change the word directly in the text field and click save.</div>
                            </div>
                        </div>
                                          
                        <div className="row" style={{ padding: "25px" }}>
                            <DataGridTable
                                tableContents={isEphemeralLoading ? ephemeralsData.ephemerals : ephemeralsData.ephemeralExceptions}
                                dataGridTableData={gridDataTable}
                                tabs={null}
                                resultsPerPage={10}
                                stateKey={isEphemeralLoading ? EPHEMERALS_KEY : EPHEMERAL_EXCEPTIONS_KEY}
                                componentInstance={isEphemeralLoading ? EPHEMERALS_KEY : EPHEMERAL_EXCEPTIONS_KEY}
                                showRowNumber={true}
                                isReadOnly={false}
                                tableActions={[{ name: ADD_NEW_ACTION }, { name: REMOVE_ACTION }]}
                                showDropdownsAsCodes={false}
                                showRowName={false}
                                sortData={sortData.bind(this)}
                                changeData={changeData.bind(this)}
                            />
                        </div>
                        {ephemeralAndExceptionValidationMessages && ephemeralAndExceptionValidationMessages.length > 0 && (
                            <div>
                                <MessageBanner resetMessageBanner={resetMessage.bind(this)} messageType={MessageType.Danger} messages={ephemeralAndExceptionValidationMessages.map(msg => msg.messageKey)} isNotClosable={false} />
                            </div>
                        )}
                        {ephemeralSaveSuccessfulRef.current && ephemeralAndExceptionValidationMessages && ephemeralAndExceptionValidationMessages.length === 0 && (
                            <div>
                                <MessageBanner resetMessageBanner={resetMessage.bind(this)} messageType={MessageType.Success} messages={["Success! Your changes have been saved."]} isNotClosable={false} />
                            </div>
                        )}
                        <div className="flexRow tableContainer flexJustifyContentEnd">
                            <div
                                className="form-group col-md-2 col-sm-4 col-xs-4">
                                <ActionButton
                                    buttonText={"Save changes"}
                                    buttonAction={() => saveChanges()}
                                    disabled={!hasUnsavedChanges}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>}
        </>
    );
}